import Axios, { AxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { setApiClient } from '@keeps/api-client';
import { isNgrokHost, NGROK_BYPASS_HEADER } from './ngrok-bypass';
import { analytics } from '@/lib/analytics';
import { version } from '../../package.json';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4500';

export const axiosInstance = Axios.create({
  baseURL: BASE_URL,
  // Repeat a bare key for array params (maxMembers=5&maxMembers=10). Axios's
  // default `maxMembers[]=5` lands under a key literally named "maxMembers[]"
  // in Express 5's simple query parser, which the API's whitelist then drops
  // silently — an unfiltered 200 rather than an error.
  paramsSerializer: { indexes: null },
});

/** See `ngrok-bypass.ts` — without this the tunnel's consent page is served
 *  instead of the API, and reads as a CORS error. */
const isNgrokApi = isNgrokHost(BASE_URL);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshToken = async (): Promise<string | null> => {
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) {
    return null;
  }

  const response = await Axios.post(`${BASE_URL}/auth/refresh`, {
    refreshToken: refresh,
  });

  const { accessToken, refreshToken: newRefreshToken } = response.data;
  localStorage.setItem('accessToken', accessToken);
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
  }

  return accessToken;
};

/** The header a guest call carries in place of a Bearer token. */
const GUEST_TOKEN_HEADER = 'X-Guest-Token';

/**
 * Case-insensitive, because axios normalises header lookups and a caller may
 * have written the name in any casing.
 */
const isGuestRequest = (config?: AxiosRequestConfig): boolean => {
  const headers = config?.headers;
  if (!headers) return false;
  if (headers instanceof AxiosHeaders) return Boolean(headers.has(GUEST_TOKEN_HEADER));
  return Object.keys(headers).some(
    (name) => name.toLowerCase() === GUEST_TOKEN_HEADER.toLowerCase()
  );
};

/**
 * Collapse the ids out of a request path so `endpoint` stays groupable.
 *
 * Raw urls (`/rolls/8f3c…/photos`) are unique per roll, which would give the
 * `api_error` breakdown one row per request instead of one per route. RN sends
 * its urls raw and pays exactly that cost; normalising here is a deliberate
 * improvement rather than a divergence in meaning.
 */
const normaliseEndpoint = (url?: string): string | undefined =>
  url
    ?.split("?")[0]
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "/:id"
    )
    .replace(/\/\d+(?=\/|$)/g, "/:id");

const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};

axiosInstance.interceptors.request.use(
  (config) => {
    // A call carrying a guest token is acting as that guest, so it must not also
    // carry a bearer. The server prefers a valid bearer over the guest token
    // (`PublicRollsController.listMembers`, `resolveUploadActor`), so sending
    // both would silently take the member path — and 403 for someone who is a
    // guest on the roll but not a member of it. Guest reads used to go through a
    // separate instance with no bearer at all; this keeps that true now that
    // they share this one.
    const token = isGuestRequest(config)
      ? null
      : localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isNgrokApi) {
      config.headers[NGROK_BYPASS_HEADER] = 'true';
    }
    // The build behind a request, for the Worker's 5xx line and its Sentry
    // event — the same header the mobile app and the Clip send.
    config.headers['X-Keeps-Client'] = `web/${version}`;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      // A guest has no account to refresh into and no session to end. Falling
      // through would `logout()` and hard-navigate them to /login mid-shoot
      // (W-35). Reject instead, and let the caller's own policy decide — for
      // photo uploads that is `classifyUploadFailure`, which keeps the photo
      // queued on a 401 rather than discarding it.
      if (isGuestRequest(originalRequest)) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        if (newToken) {
          processQueue(null, newToken);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return axiosInstance(originalRequest);
        } else {
          processQueue(new Error('No refresh token available'), null);
          logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // The catch-all: everything that was not a recoverable 401 above. A
    // cancelled query is not a failure — react-query cancels on unmount, and
    // counting those would bury the real ones.
    if (!Axios.isCancel(error)) {
      const status = error.response?.status;
      const report = {
        endpoint: normaliseEndpoint(error.config?.url),
        statusCode: status,
        message: error.message,
        code: error.code,
        requestId: error.response?.headers?.['x-request-id'] as string | undefined,
      };
      // Sentry gets what is ours to fix: the server failing. A 4xx is the
      // server saying no on purpose, and no answer at all is the visitor's
      // connection; both stay analytics.
      if (status !== undefined && status >= 500)
        analytics.reportError("api_error", error, report);
      else analytics.capture("api_error", report);
    }

    return Promise.reject(error);
  }
);

/**
 * Hand the instance to `@keeps/api-client`, which owns none of its own — every
 * generated call runs through whatever is registered here.
 *
 * At module scope, and `app/providers/QueryProvider.tsx` imports this file for
 * the side effect, so registration happens before any query can run. Safe on
 * the server too: nothing above touches `localStorage` outside an interceptor
 * closure, which is what already lets this file survive Next's server pass.
 *
 * The `CancelToken` the old mutator attached is gone with it. React Query v5
 * cancels through `AbortSignal` and no longer calls `promise.cancel()`, so it
 * had no caller; the `Axios.isCancel` guard in the interceptor above still
 * matches, because an aborted request rejects with `CanceledError` either way.
 */
setApiClient(axiosInstance);
